import React from "react";
import { useTranslation } from "react-i18next";
import { GARMatrixRow } from "../../../services/HigherAwardTracking/HigherAwardTrackingAPI.types";
import defaultContent from "../../../content/hat";
import { useAppSelector } from "../../../store";
import { getPerformanceYearShort } from "../../../common/util/period";
import "./GARMatrix.scss";
import LeafPinLevelName from "../../../common/leaf/LeafPinLevelName/LeafPinLevelName";

type Props = {
  rows: GARMatrixRow[];
  isChinaAbo: boolean;
};

const GARMatrix = ({ rows, isChinaAbo }: Props) => {
  const { t } = useTranslation(["hat", "awardLabels"]);
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const performanceYear = getPerformanceYearShort(selectedPeriod);

  const getGarDisclaimer = (disclaimerContentKey: string) => {
    let defaultText = defaultContent["modalGARViewYourStatus"];
    for (const part of disclaimerContentKey.split(".")) {
      defaultText = defaultText[part];
    }

    return t("modalGARViewYourStatus." + disclaimerContentKey, defaultText as string);
  };

  return (
    <div className="GARMatrix">
      {rows && rows.length > 0 && (
        <table>
          <tbody>
            <tr className="tracking-table_header">
              <th>
                {t(
                  "modalGARViewYourStatus.awardLevel",
                  defaultContent["modalGARViewYourStatus"]["awardLevel"],
                ).toString()}
              </th>
              <th>
                {t(
                  "modalGARViewYourStatus.totalLegsCounting",
                  defaultContent["modalGARViewYourStatus"]["totalLegsCounting"],
                ).toString()}
              </th>
              <th colSpan={2}>
                <div className="GARMatrix__desktop">
                  {t(
                    "modalGARViewYourStatus.qualificationCredits",
                    defaultContent["modalGARViewYourStatus"]["qualificationCredits"],
                  ).toString()}
                </div>
                <div className="GARMatrix__mobile">
                  {t("modalGARViewYourStatus.qc", defaultContent["modalGARViewYourStatus"]["qc"]).toString()}
                </div>
              </th>
            </tr>
            {rows.map((row, index) => {
              const rowClass = row.tierRowClass + (row.disclaimer ? " GARMatrix__disclamer-row" : "");
              if (row.awardClass === "blank-cell last") {
                return <React.Fragment key={`row-${index}`}></React.Fragment>;
              }

              return (
                <React.Fragment key={`row-${index}`}>
                  <tr className={rowClass}>
                    {row.disclaimer ? (
                      <td colSpan={4}>{getGarDisclaimer(row.disclaimerContentKey)}</td>
                    ) : (
                      <>
                        <td className={row.awardClass}>
                          {isChinaAbo
                            ? t(
                                "modalGARViewYourStatus.awardLabels.chinaLabels.award" + row.awardRank,
                                defaultContent["modalGARViewYourStatus"]["awardLabels"]["chinaLabels"][
                                  "award" + row.awardRank
                                ],
                              ).toString()
                            : t(
                                "modalGARViewYourStatus.awardLabels.award" + row.awardRank,
                                defaultContent["modalGARViewYourStatus"]["awardLabels"]["award" + row.awardRank],
                              ).toString()}
                        </td>
                        <td className={row.fpLegClass}>{row.fpLegLabel}</td>
                        <td className={row.qualCreditClass}>{row.qualCreditLabel}</td>
                        {row.qcLegRowspan > 0 && (
                          <td className={row.qcLegClass} rowSpan={row.qcLegRowspan}>
                            {t(
                              "modalGARViewYourStatus." + row.qcLegContentKey,
                              defaultContent["modalGARViewYourStatus"][row.qcLegContentKey],
                              { qcCount: row.maxCountPerLeg },
                            ).toString()}
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                  {row.disclaimer && (
                    <tr key={`spacer-${index}`} className="GARMatrix__spacer-row" aria-hidden="true">
                      <td colSpan={4} aria-label="spacer"></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default GARMatrix;
